/**
 * @file カートに関するストア
 */

import { defineStore } from 'pinia'

const LOCAL_STORAGE_KEY = 'cart'

export const useCartStore = defineStore({
  id: 'cart',

  state: () => ({
    /**
     * カートのデータ
     *
     * 「学校ID+請求コード」の文字列を配列で保存する。
     *
     * e.g.
     * `['D100001_100001', 'T100001_100001', 'S100001_100001']`
     *
     * **⚠ LocalStorageと同期するために、`watch`などを使用して変更を監視し、変更直後に`store()`を呼び出すようにしてください。**
     *
     * `add()`, `remove()`, `clear()`を介して変更した場合は、`store()`を呼び出す必要はありません。
     */
    list: [] as string[],
  }),

  getters: {},

  actions: {
    /**
     * 初期化する
     *
     * 最初に実行するようにしてください。
     *
     * 1. LocalStorageからデータを取得
     * 2. 取得したデータの整合性を確認
     * 3. データをストアへ保持
     */
    init() {
      try {
        // LocalStorageからデータを取得。空ならエラーでcatch節へ
        const data = localStorage.getItem(LOCAL_STORAGE_KEY)
        if (!data) throw null

        // データをパース。パース失敗ならエラーでcatch節へ
        const parsed = JSON.parse(data)

        // 配列かどうかの判定。違うならエラーでcatch節へ
        if (!Array.isArray(parsed)) throw null

        // 文字列データのみに絞り込む（想定外のデータが混入していた場合にはじく）
        const filtered = parsed.filter((row): row is string => typeof row === 'string')

        // ストアへ保持
        this.list = filtered
      } catch {
        // 何らかのエラーの場合は、LocalStorageに初期化済みのデータを格納する
        this.store()
      }
    },

    /**
     * カートに追加する
     *
     * e.g.
     * `add('D100001')`
     */
    add(val: string) {
      const set = new Set(this.list)
      set.add(val)
      this.list = [...set.values()]
      this.store()
    },

    /**
     * カートから削除する
     *
     * e.g.
     * `remove('D100001')`
     */
    remove(val: string) {
      const set = new Set(this.list)
      set.delete(val)
      this.list = [...set.values()]
      this.store()
    },

    /** カートを空にする */
    clear() {
      this.list = []
      localStorage.removeItem(LOCAL_STORAGE_KEY)
    },

    /** 現在のカートのデータをLocalStorageへ保存する */
    store() {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(this.list))
    },
  },
})
